// src/ui/components/dialogs/FullscreenDialog.tsx
import React, { useRef } from 'react';
import ScaledTextWrapper from '../../../core/utils/ScaledTextWrapper';
import { useScreenResolution } from '../../../core/hooks/useScreenResolution';
import { FullscreenDialogProps } from './FullscreenDialog.types';
import {
  processHtml,
  getTextScale,
  isImageMedia,
  extractImageUrl,
} from './FullscreenDialog.utils';
import {
  useLazyMedia,
  useBodyScrollLock,
  useDialogCloseEvents,
  useDialogRequestClose,
  useDialogOpenClose,
} from './FullscreenDialog.hooks';
import { RenderFullscreenMedia } from './FullscreenDialog.media';
import {
  TextOnlyLayout,
  ImageLayout,
  VideoLayout,
  NormalLayout,
} from './FullscreenDialog.layouts';

const FullscreenDialog: React.FC<FullscreenDialogProps> = ({
  title,
  mediaHtml,
  sanitizedWork = '',
  parserOptions,
  showExpanded,
  showFullScreen,
  gridClass = 'grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-stretch',
  onRequestClose,
  blockSpecs,
}) => {
  const idRef = useRef(`fsdlg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const supportsDialog = typeof window !== 'undefined' && 'HTMLDialogElement' in window;
  const internalSwitchingRef = useRef(false);

  // Hooks personalizados
  const shouldLoadMedia = useLazyMedia(showExpanded, showFullScreen);
  const screenResolution = useScreenResolution();
  useBodyScrollLock(showExpanded, showFullScreen, dialogRef);
  useDialogCloseEvents(supportsDialog, dialogRef, showExpanded, showFullScreen);
  useDialogRequestClose(
    supportsDialog,
    dialogRef,
    idRef,
    internalSwitchingRef,
    onRequestClose,
  );
  useDialogOpenClose(
    supportsDialog,
    dialogRef,
    idRef,
    internalSwitchingRef,
    showExpanded,
    showFullScreen,
  );

  // Procesar HTML
  const processedWork = processHtml(sanitizedWork);

  // Handlers
  const handleClose = () => {
    if (supportsDialog) {
      dialogRef.current?.close();
    } else {
      if (typeof onRequestClose === 'function') onRequestClose();
    }
  };

  // Props comunes para layouts
  const commonLayoutProps = {
    title,
    processedWork,
    parserOptions,
    blockSpecs,
    screenResolution: {
      isLargeScreen: screenResolution.isLargeScreen,
      textScale: screenResolution.textScale,
    },
  };

  // Renderizar contenido según el modo
  const renderContent = () => {
    if (showFullScreen) {
      return (
        <div className="w-full h-screen flex items-center justify-center">
          <RenderFullscreenMedia
            html={mediaHtml}
            parserOptions={parserOptions}
            shouldLoadMedia={shouldLoadMedia}
            title={title}
          />
        </div>
      );
    }

    if (showExpanded) {
      if (!mediaHtml) {
        return <TextOnlyLayout {...commonLayoutProps} />;
      }

      if (isImageMedia(mediaHtml)) {
        return <ImageLayout {...commonLayoutProps} imageUrl={extractImageUrl(mediaHtml)} />;
      }

      return (
        <VideoLayout
          {...commonLayoutProps}
          mediaHtml={mediaHtml}
          shouldLoadMedia={shouldLoadMedia}
        />
      );
    }

    // Modo normal
    return (
      <ScaledTextWrapper scale={getTextScale()}>
        <NormalLayout
          processedWork={processedWork}
          parserOptions={parserOptions}
          mediaHtml={mediaHtml}
          gridClass={gridClass}
          shouldLoadMedia={shouldLoadMedia}
          isImageMedia={isImageMedia}
          extractImageUrl={extractImageUrl}
        />
      </ScaledTextWrapper>
    );
  };

  return (
    <>
      {supportsDialog ? (
        <dialog
          ref={dialogRef}
          style={{ maxHeight: 'none' }}
          className="p-0 m-0 bg-transparent border-32"
          onClose={() => { }}
        >
          <div className="fixed inset-0 z-[9999] overflow-hidden">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-xl"
              onClick={handleClose}
              aria-hidden
            />
            <div
              className="absolute inset-0 z-10 w-full h-full bg-black/40 overflow-hidden text-left"
            >
              {renderContent()}
            </div>
          </div>

        </dialog>
      ) : (
        showExpanded && (
          <div className="fixed inset-0 z-[9999] w-full h-full">
            <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
            <div className="relative z-10 w-full h-full">
              <div className="bg-black/40 w-full h-full overflow-hidden backdrop-blur-lg">
                {!mediaHtml ? (
                  <TextOnlyLayout {...commonLayoutProps} />
                ) : isImageMedia(mediaHtml) ? (
                  <ImageLayout {...commonLayoutProps} imageUrl={extractImageUrl(mediaHtml)} />
                ) : (
                  <VideoLayout
                    {...commonLayoutProps}
                    mediaHtml={mediaHtml}
                    shouldLoadMedia={shouldLoadMedia}
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default FullscreenDialog;
